import wkt from "wkt";
import JSZip from "jszip";
import { read } from "shapefile";
import * as topojson from "topojson-client"; // npm install topojson-client
import * as toGeoJSON from "@mapbox/togeojson"; // npm install @mapbox/togeojson
import { parse as csvParse } from "csv-parse/browser/esm/sync"; // npm install csv-parse
import shpwrite from "@mapbox/shp-write";
import { Feature, FeatureCollection, Point, Polygon } from "geojson";
import mapboxgl from "mapbox-gl";

import { showToastMessage } from "../../../utils/commonFunctions";
import { ActiveToolType, FeatureConfigType, GeoFeatureDataType, SelectedFeatureType } from "./types";
import { isEmpty } from "lodash";

export const POLYGON_TYPES = ["Polygon", "Geofence"]

const EMPTY_FC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

/**
 * Adds hover sources, layers, and mouse event handlers to a Mapbox map.
 * Returns a cleanup function that removes all added sources, layers, and listeners.
 */
export const setupMapHoverEffects = (map: mapboxgl.Map): (() => void) => {
    map.addSource("draw-hover", { type: "geojson", data: EMPTY_FC });

    map.addLayer({
        id: "draw-hover-fill",
        type: "fill",
        source: "draw-hover",
        paint: { "fill-color": "#090909", "fill-opacity": 0.4 }
    });

    map.addLayer({
        id: "draw-hover-outline",
        type: "line",
        source: "draw-hover",
        paint: { "line-color": "#FFFFFF", "line-width": 1, "line-dasharray": [1, 2] }
    });

    map.addSource("hover-ring", { type: "geojson", data: EMPTY_FC });

    map.addLayer({
        id: "hover-ring-layer",
        type: "circle",
        source: "hover-ring",
        paint: {
            "circle-radius": 8,
            "circle-color": "rgba(0,0,0,0)",
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 1,
            "circle-stroke-opacity": 0.8,
        }
    });

    const onPolygonHover = (e: mapboxgl.MapMouseEvent) => {
        const features = map.queryRenderedFeatures(e.point, {
            layers: ["gl-draw-polygon-fill-inactive.cold", "gl-draw-polygon-fill-inactive.hot"]
        });
        const source = map.getSource("draw-hover") as mapboxgl.GeoJSONSource;
        if (features.length > 0) {
            map.getCanvas().style.cursor = "pointer";
            source.setData({ type: "FeatureCollection", features: [features[0]] });
        } else {
            map.getCanvas().style.cursor = "";
            source.setData(EMPTY_FC);
        }
    };

    const onPointHover = (e: mapboxgl.MapMouseEvent) => {
        const features = map.queryRenderedFeatures(e.point, {
            layers: ["gl-draw-point-inactive-outer.cold", "gl-draw-point-inactive-outer.hot"]
        });
        const source = map.getSource("hover-ring") as mapboxgl.GeoJSONSource;
        source.setData(features.length ? { type: "FeatureCollection", features: [features[0]] } : EMPTY_FC);
    };

    const onPointLeave = () => {
        (map.getSource("hover-ring") as mapboxgl.GeoJSONSource).setData(EMPTY_FC);
    };

    map.on("mousemove", onPolygonHover);
    map.on("mousemove", onPointHover);
    map.on("mouseleave", "gl-draw-point-inactive-outer.cold", onPointLeave);

    return () => {
        map.off("mousemove", onPolygonHover);
        map.off("mousemove", onPointHover);
        map.off("mouseleave", "gl-draw-point-inactive-outer.cold", onPointLeave);
        if (map.getLayer("draw-hover-fill")) map.removeLayer("draw-hover-fill");
        if (map.getLayer("draw-hover-outline")) map.removeLayer("draw-hover-outline");
        if (map.getLayer("hover-ring-layer")) map.removeLayer("hover-ring-layer");
        if (map.getSource("draw-hover")) map.removeSource("draw-hover");
        if (map.getSource("hover-ring")) map.removeSource("hover-ring");
    };
};

export const getGeometry = (feature: GeoFeatureDataType) => {
    const geometry = {
        type: POLYGON_TYPES.includes(feature?.type) ? 'Polygon' : 'Point',
        coordinates: POLYGON_TYPES.includes(feature?.type) ? [feature?.polygon || []] : [feature?.coordinates?.long, feature?.coordinates?.lat],
    }
    return geometry
}

export const toggleMinimizeModal = (className: string) => {
    const modal = document.querySelector(`.${className}`)
    if (modal) {
        modal.classList.toggle('minimize')
    }
}

export const checkLatLongValidation = (type: string, value: string) => {
    const label = type === "lat" ? "Latitude" : "Longitude";
    const min = type === "lat" ? -90 : -180;
    const max = type === "lng" ? 180 : 180;
    const decimalPart = value.split(".")[1];
    if (Number(value) > max || Number(value) < min) {
        showToastMessage("error", `${label} Must be between ${min} and ${max}`);
        return false
    }
    else if (decimalPart && decimalPart.length > 8) {
        showToastMessage("error", "Latitude cannot have more than 8 decimal places");
        return false
    }
    return true
}

export const getAoiName = (type: ActiveToolType, allFeatures: GeoFeatureDataType[] = []): string => {
    if (type === 'Point' || type === 'Polygon') {
        const regex = type === 'Point' ? /^Point (\d+)$/ : /^Area of Interest (\d+)$/;
        const numbers = allFeatures
            .map(feature => {
                const match = feature.name.match(regex);
                return match ? parseInt(match[1], 10) : null;
            })
            .filter(num => num !== null) as number[];
        return numbers.length ? type === 'Point' ? `Point ${Math.max(...numbers) + 1}` : `Area of Interest ${Math.max(...numbers) + 1}` : type === 'Point' ? `Point 1` : `Area of Interest 1`;
    } else {
        return ''
    }
};


export const handleFileChange = async (): Promise<{
    name: string;
    type: string;
    data: GeoJSON.FeatureCollection | GeoJSON.FeatureCollection[] | null
} | null> => {
    return new Promise((resolve, reject) => {
        // Dynamically create input
        const input = document.createElement("input");
        input.type = "file";
        input.accept = ".zip,.geojson,.json,.topojson,.csv,.kml,.wkt";
        input.style.display = "none";

        input.onchange = async (event: any) => {
            const file = event.target.files?.[0];
            if (!file) return resolve(null);

            const fileSizeLimit = 1048576; // 1 MB
            if (file.size === 0) {
                showToastMessage("error", "File is empty");
                return resolve(null);
            }
            if (file.size > fileSizeLimit) {
                showToastMessage("error", "File exceeds the size limit (1 MB)");
                return resolve(null);
            }

            const fileName = file.name.toLowerCase();
            const reader = new FileReader();

            reader.onload = async () => {
                const arrayBuffer = reader.result;
                const file_data: {
                    name: string;
                    type: string;
                    data: any;
                } = {
                    name: fileName,
                    type: "",
                    data: null,
                };

                try {
                    if (fileName.endsWith(".zip")) {
                        const isZipFile = (buffer: ArrayBuffer) => {
                            const signature = new Uint8Array(buffer.slice(0, 4));
                            return (
                                signature[0] === 0x50 &&
                                signature[1] === 0x4b &&
                                signature[2] === 0x03 &&
                                signature[3] === 0x04
                            );
                        };

                        if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) {
                            showToastMessage("error", "Failed to read ZIP file");
                            return resolve(null);
                        }

                        if (!isZipFile(arrayBuffer)) {
                            showToastMessage("error", "Not a valid ZIP archive");
                            return resolve(null);
                        }

                        const zip = await JSZip.loadAsync(arrayBuffer);
                        const extractedFiles: Record<string, string> = {};
                        for (const [relativePath, zipEntry] of Object.entries(zip.files)) {
                            if (!zipEntry.dir) {
                                extractedFiles[relativePath] = await zipEntry.async("string");
                            }
                        }
                        file_data.type = "zip";
                        file_data.data = arrayBuffer;
                    } else if (fileName.endsWith(".geojson") || fileName.endsWith(".json")) {
                        const text = new TextDecoder("utf-8").decode(arrayBuffer as ArrayBuffer);
                        const geoData = JSON.parse(text);
                        if (geoData.type === "FeatureCollection" && Array.isArray(geoData.features)) {
                            file_data.type = "geojson";
                            file_data.data = arrayBuffer;
                        } else {
                            showToastMessage("error", "Invalid GeoJSON file");
                            return resolve(null);
                        }
                    } else if (fileName.endsWith(".topojson")) {
                        const text = new TextDecoder("utf-8").decode(arrayBuffer as ArrayBuffer);
                        const topoData = JSON.parse(text);
                        if (topoData.type === "Topology" && topoData.objects) {
                            file_data.type = "topojson";
                            file_data.data = arrayBuffer;
                        } else {
                            showToastMessage("error", "Invalid TopoJSON file");
                            return resolve(null);
                        }
                    } else if (fileName.endsWith(".csv")) {
                        const text = new TextDecoder("utf-8").decode(arrayBuffer as ArrayBuffer);
                        const rows = text.split("\n").map((r) => r.split(","));
                        if (rows.length > 1 && rows[0].length > 1) {
                            file_data.type = "csv";
                            file_data.data = arrayBuffer;
                        } else {
                            showToastMessage("error", "Invalid CSV file");
                            return resolve(null);
                        }
                    } else if (fileName.endsWith(".kml")) {
                        const text = new TextDecoder("utf-8").decode(arrayBuffer as ArrayBuffer);
                        const xmlDoc = new DOMParser().parseFromString(text, "application/xml");
                        if (xmlDoc.getElementsByTagName("kml").length > 0) {
                            file_data.type = "kml";
                            file_data.data = arrayBuffer;
                        } else {
                            showToastMessage("error", "Invalid KML file");
                            return resolve(null);
                        }
                    } else if (fileName.endsWith(".wkt")) {
                        file_data.type = "wkt";
                        file_data.data = arrayBuffer;
                    } else {
                        showToastMessage("error", "File format not supported");
                        return resolve(null);
                    }
                    const result = await processFile(file_data);
                    resolve({
                        name: fileName,
                        type: file_data.type,
                        data: result
                    });
                } catch (err) {
                    console.error("Error processing file:", err);
                    showToastMessage("error", "Failed to process file");
                    resolve(null);
                }
            };

            reader.readAsArrayBuffer(file);
        };

        // Trigger picker
        input.click();
    });
};

/**
 * Process a file into GeoJSON
 */
export const processFile = async (
    storedFile: {
        name: string;
        type: string;
        data: any
    }
): Promise<GeoJSON.FeatureCollection | GeoJSON.FeatureCollection[] | null> => {
    try {
        const arrayBuffer = await storedFile.data;
        const text = new TextDecoder("utf-8").decode(arrayBuffer);
        const fileType = storedFile.type

        // --- GeoJSON / JSON ---
        if (['geojson', 'json'].includes(fileType)) {
            return JSON.parse(text);
        }

        // --- TopoJSON ---
        if (fileType === "topojson") {
            const topoData = JSON.parse(text);
            return topojson.feature(
                topoData,
                topoData.objects[Object.keys(topoData.objects)[0]]
            );
        }

        // --- CSV ---
        if (fileType === "csv") {
            const rows = csvParse(text, { columns: true, skip_empty_lines: true });

            if (!rows.length || !("lat" in rows[0]) || !("lon" in rows[0])) {
                showToastMessage("error", "Invalid CSV file: missing 'lat' and 'lon'");
                return null;
            }
            return {
                type: "FeatureCollection",
                features: rows.map((row: any) => ({
                    type: "Feature",
                    properties: row,
                    geometry: {
                        type: "Point",
                        coordinates: [parseFloat(row.lon), parseFloat(row.lat)],
                    },
                })),
            };
        }

        // --- KML ---
        if (fileType === "kml") {
            const parser = new DOMParser();
            const kmlDoc = parser.parseFromString(text, "application/xml");
            return toGeoJSON.kml(kmlDoc);
        }

        // --- WKT ---
        if (fileType === "wkt") {
            const geojson = wkt.parse(text);
            return {
                type: "FeatureCollection",
                features: [{
                    type: "Feature",
                    properties: {},
                    geometry: {
                        type: "Point",
                        coordinates: geojson.coordinates,
                    },
                }],
            };
        }

        // --- Shapefile (ZIP) ---
        if (fileType === "zip") {
            const zip = await JSZip.loadAsync(arrayBuffer);
            const files = Object.values(zip.files);

            const geojsonFiles: any[] = [];
            let shpFileFound = false;

            // Group files by base name
            const fileGroups: Record<string, Record<string, JSZip.JSZipObject>> = {};
            files.forEach((file) => {
                const baseName = file.name.replace(/\.[^.]+$/, "");
                if (!fileGroups[baseName]) fileGroups[baseName] = {};
                const ext = file.name.split(".").pop()?.toLowerCase() || "";
                fileGroups[baseName][ext] = file;
            });

            for (const baseName in fileGroups) {
                const group = fileGroups[baseName];
                if (group.shp) {
                    shpFileFound = true;

                    const shpBuffer = await group.shp.async("arraybuffer");
                    const dbfBuffer = group.dbf ? await group.dbf.async("arraybuffer") : null;
                    const prjText = group.prj ? await group.prj.async("string") : null;

                    let geojson;
                    if (dbfBuffer) {
                        geojson = await read(shpBuffer, dbfBuffer);
                    } else {
                        geojson = await read(shpBuffer);
                    }

                    if (prjText) {
                        geojson.crs = prjText;
                    }

                    geojson.features.forEach((f: any, i: number) => {
                        if (!f.id) f.id = `${baseName}_${i + 1}`;
                    });

                    geojsonFiles.push({ name: baseName, geojson });
                }
            }

            if (!shpFileFound) {
                showToastMessage("error", "No .shp file found in ZIP");
                return null;
            }

            return geojsonFiles.length === 1
                ? geojsonFiles[0].geojson
                : geojsonFiles.map((g) => g.geojson);
        }

        // --- Unsupported ---
        showToastMessage("error", "Unsupported file format");
        return null;
    } catch (err) {
        console.error("Error processing file:", err);
        showToastMessage("error", "Error processing file");
        return null;
    }
};

export const getUploadedFileData = (uploadedGeoJSON: any) => {
    const featureList: {
        type: string;
        coordinates: any;
        name: string;
        description: string;
    }[] = [];

    const processFeature = (featureItem: any) => {
        if (!featureItem?.geometry) return;

        const { type, coordinates } = featureItem.geometry;

        // Polygon or MultiPolygon
        const name = featureItem?.properties?.name ?? ""
        const description = featureItem?.properties?.descriptio ?? featureItem?.properties?.description ?? ""
        if (type === "Polygon" || type === "MultiPolygon") {
            coordinates.forEach((coordinateSet: any) => {
                if (
                    Array.isArray(coordinateSet) &&
                    coordinateSet.every(
                        (coord: any) => Array.isArray(coord) && coord.length === 2
                    )
                ) {
                    sessionStorage.setItem("flatArrayFlag", "true");
                }

                featureList.push({
                    type: 'Polygon',
                    coordinates: coordinateSet,
                    name: name,
                    description: description,
                });
            });
        } else if (type === "Point") {
            featureList.push({
                type,
                coordinates: [coordinates[0], coordinates[1]],
                name: name,
                description: description,
            });
        }
    };

    // Normalize into an array of FeatureCollections
    const collections = Array.isArray(uploadedGeoJSON)
        ? uploadedGeoJSON
        : [uploadedGeoJSON];

    collections.forEach((collection) => {
        collection?.features?.forEach(processFeature);
    });

    return featureList;
};



export const getUpdatedFileData = (selectedFeature: SelectedFeatureType | null, allFeatures: GeoFeatureDataType[]) => {
    if (!selectedFeature) return;
    let formData = {}
    const featureList: FeatureConfigType[] = [];
    const data = selectedFeature?.data as FeatureConfigType;
    const activeTool = selectedFeature?.activeTool;
    if (activeTool === 'File') {
        const fileName = data?.name
        const polygonName = getAoiName('Polygon', allFeatures)
        const pointName = getAoiName('Point', allFeatures)
        const polygonMatch = polygonName.match(/\d+/);
        const pointMatch = pointName.match(/\d+/);
        let maxPolygonNumber = polygonMatch ? parseInt(polygonMatch[0], 10) : 1;
        let maxPointNumber = pointMatch ? parseInt(pointMatch[0], 10) : 1;
        const files = data?.file || [];
        const allFeatureNames = allFeatures.map(feature => feature.name)
        files.forEach((detail) => {
            const isPolygon = detail && (detail.type === 'MultiPolygon' || detail.type === 'Polygon');
            const defaultData = {
                "name": (!isEmpty(detail.name) && !allFeatureNames.includes(detail.name)) ? detail.name : isPolygon ? `Area of Interest ${maxPolygonNumber}` : `Point ${maxPointNumber}`,
                "type": isPolygon ? 'Geofence' : 'Target Track',
                "description": detail.description || '',
            }
            if (isPolygon) {
                defaultData['polygon'] = detail.coordinates.length === 1 ? detail.coordinates[0] : detail.coordinates
                if ((isEmpty(detail.name) || allFeatureNames.includes(detail.name))) {
                    maxPolygonNumber++
                }
            } else {
                defaultData['coordinates'] = {
                    "altitude": 0,
                    "lat": detail.coordinates[1],
                    "long": detail.coordinates[0],
                    "minElevationAngle": 0
                }
                if ((isEmpty(detail.name) || allFeatureNames.includes(detail.name))) {
                    maxPointNumber++
                }
            }
            featureList.push(defaultData)
        })
        formData = {
            areaOfInterests: featureList,
            "name": fileName,
            "type": "Collection"
        };
    }

    return formData
}

export const downloadAsGeoJSON = (features: GeoFeatureDataType[]) => {
    if (!features) return;
    const geojsonData: {
        id: any;
        type: string;
        geometry: {
            type: string;
            coordinates: [number, number][][] | number[];
        };
        properties: {};
    }[] = []
    const name = features.length === 1 ? features[0]?.name : "all-features";
    features.forEach((feature) => {
        const data = {
            id: feature?.areaOfInterestId,
            type: "Feature",
            geometry: getGeometry(feature),
            properties: {},
        };
        geojsonData.push(data);
    })
    const body = {
        "type": "FeatureCollection",
        "features": geojsonData
    }
    // Convert to blob
    const blob = new Blob([JSON.stringify(body, null, 2)], {
        type: "application/geo+json",
    });

    // Trigger download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${name}.geojson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const downloadAsShapefile = (features: GeoFeatureDataType[]) => {
    const name = features.length === 1 ? features[0]?.name : "all-features";

    // Build features array
    const geojsonFeatures = features.map((feature) => {
        const geometry = getGeometry(feature);
        return {
            id: feature?.areaOfInterestId,
            type: "Feature",
            geometry,
            properties: {
                name: feature?.name ?? "",
                type: feature?.type ?? "",
                description: feature?.description ?? "",
            },
        };
    });

    // Build FeatureCollection
    const body: FeatureCollection<Point | Polygon> = {
        type: "FeatureCollection",
        features: geojsonFeatures as Feature<Point | Polygon>[],
    };

    // Download shapefile ZIP
    shpwrite.download(body, {
        folder: name,
        types: {
            point: "points",
            polygon: "polygons",
        },
        compression: "STORE",
        outputType: "blob"
    });
};
